import React, { useState } from 'react';
import styles from './TypeOfLossView.module.scss';
import { GeneratingExtraction, ServiceOptions } from 'app/components';
import { processFileAndGetTypeOfLoss } from 'integration';
import {
  displayToastError,
  handleFileSelection,
  handleFileValidation
} from 'app/utils';
import {
  getBusinessArea,
  getService,
  setGeneratingExtraction,
  useAppSelector
} from 'app/redux';
import { Button } from '@zurich-primitives/button';
import { ReactComponent as VectorRight } from 'app/assets/Icons/VectorRight.svg';
import { useDispatch } from 'react-redux';
import {
  ResultSection,
  UploadSection
} from 'app/components/Sections/TypeOfLoss';
import { ITOLFileHandlers } from './TypeOfLossView.interface';

export const TypeOfLossView: React.FC = () => {
  const AVG_DURATION_IN_MS = 120000;
  const dispatch = useDispatch();
  const currentService = useAppSelector(getService) ?? 'type-of-loss';
  const currentBusinessArea = useAppSelector(getBusinessArea) ?? 'claims';

  const [mainFile, setMainFile] = useState<File | undefined>(undefined);
  const [trainFile, setTrainFile] = useState<File | undefined>(undefined);

  const [mainFileError, setMainFileError] = useState<string | undefined>();
  const [trainFileError, setTrainFileError] = useState<string | undefined>();

  const [mainIsDragging, setMainIsDragging] = useState(false);
  const [trainIsDragging, setTrainIsDragging] = useState(false);

  const [currentView, setCurrentView] = useState<'upload' | 'result'>('upload');
  const [resultFileName, setResultFileName] = useState<string | undefined>(
    undefined
  );
  const [resultFile, setResultFile] = useState<Blob | undefined>(undefined);
  const [resultPreview, setResultPreview] = useState<any[]>([]);
  const [resultTotalRows, setResultTotalRows] = useState<number | undefined>(
    undefined
  );

  const fileHandlers = (
    setDocument: (file: File | undefined) => void,
    setIsDragging: (dragging: boolean) => void,
    setError: (error: string | undefined) => void
  ): ITOLFileHandlers => {
    const handleUploadFile = (e: { stopPropagation: () => void }): void => {
      e.stopPropagation();
      setError(undefined);
      handleFileSelection(handleFileChange, currentService);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
      const { files } = e.target;
      const selectedFile: File | undefined = files ? files[0] : undefined;

      if (selectedFile === undefined) return;
      const validation = handleFileValidation(selectedFile, currentService);
      if (validation.valid) {
        setDocument(selectedFile);
      } else {
        const errorMessage = validation.error;
        console.error(errorMessage);
        setError(errorMessage);
      }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];

      if (!file) return;
      const validation = handleFileValidation(file, currentService);
      if (validation.valid) {
        setDocument(file);
      } else {
        const errorMessage = validation.error;
        console.error(errorMessage);
        setError(errorMessage);
      }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      setError(undefined);
    };

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>): void => {
      e.preventDefault();
      setIsDragging(false);
    };

    const handleDeleteFile = (): void => {
      setDocument(undefined);
      setError(undefined);
    };

    return {
      handleUploadFile,
      handleDrop,
      handleDragOver,
      handleDragEnter,
      handleDragLeave,
      handleDeleteFile
    };
  };

  const handleGetResults = (): void => {
    dispatch(setGeneratingExtraction(true));
    processFileAndGetTypeOfLoss(
      currentBusinessArea,
      currentService,
      mainFile!,
      trainFile
    )
      .then((response) => {
        const { datasetId, preview, totalRows, fileBase64 } = response;
        const fileName = `${datasetId}_type_of_loss.xlsx`;
        const byteCharacters = atob(fileBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          const cp = byteCharacters.codePointAt(i);
          if (cp === undefined) continue;
          byteNumbers[i] = cp;
        }
        const byteArray = new Uint8Array(byteNumbers);
        const file = new Blob([byteArray], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        setResultFileName(fileName);
        setResultFile(file);
        setResultPreview(preview);
        setResultTotalRows(totalRows);

        setCurrentView('result');
      })
      .catch((error) => {
        console.error('Error:', error);
        displayToastError(error.message, 'Process file and get type of loss');
      })
      .finally(() => {
        dispatch(setGeneratingExtraction(false));
      });
  };

  const renderUploadSection = (): React.JSX.Element => {
    return (
      <div className={styles['upload-section']}>
        <div className={styles['main-file']}>
          <div className={styles['file-info']}>
            <p className={styles.label}>
              Claims file <span className={styles.required}>*</span>
            </p>
            <p className={styles.description}>
              Upload an .xlsx file that includes the columns <em>claim_id</em>{' '}
              and <em>claim_description</em>.
            </p>
          </div>

          <UploadSection
            file={mainFile}
            isDragging={mainIsDragging}
            errorText={mainFileError}
            fileHandlers={fileHandlers(
              setMainFile,
              setMainIsDragging,
              setMainFileError
            )}
            uploadText="Drag and drop your .xlsx file here, or click to upload"
          />
        </div>

        <div className={styles['train-file']}>
          <div className={styles['file-info']}>
            <p className={styles.label}>
              Training file <span className={styles.optional}>(optional)</span>
            </p>

            <p className={styles.description}>
              Optionally include a sample .xlsx file with existing
              classifications to improve accuracy. The file must include:{' '}
              <em>claim_id</em>, <em>claim_description</em> and either{' '}
              <em>type_of_loss_local</em> or <em>type_of_loss_suggested</em>.
            </p>
          </div>

          <UploadSection
            file={trainFile}
            isDragging={trainIsDragging}
            errorText={trainFileError}
            fileHandlers={fileHandlers(
              setTrainFile,
              setTrainIsDragging,
              setTrainFileError
            )}
            uploadText="Drag and drop your .xlsx file here, or click to upload"
          />
        </div>

        <Button
          onClick={handleGetResults}
          disabled={!mainFile}
          className={styles.button}
        >
          <Button.Label>Classify claims</Button.Label>
          <Button.Icon>
            <VectorRight />
          </Button.Icon>
        </Button>
      </div>
    );
  };

  const onNewClassification = (): void => {
    setMainFile(undefined);
    setTrainFile(undefined);
    setResultFileName(undefined);
    setResultFile(undefined);
    setResultPreview([]);
    setResultTotalRows(undefined);

    setCurrentView('upload');
  };

  return (
    <div className={styles['type-of-loss-view']}>
      <ServiceOptions />

      <div className={styles.header}>
        <p>
          Upload your claims file to automatically determine the type of loss
        </p>
      </div>

      <div className={styles.body}>
        {currentView === 'result' && (
          <ResultSection
            fileName={resultFileName!}
            file={resultFile!}
            preview={resultPreview}
            totalRows={resultTotalRows!}
            onNewClassification={onNewClassification}
          />
        )}
        {currentView === 'upload' && renderUploadSection()}
      </div>

      <GeneratingExtraction
        processTime={AVG_DURATION_IN_MS}
        file={mainFile}
        caption="Please note that loading times may be slightly longer for larger files."
      />
    </div>
  );
};
